import React, { useContext } from "react";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import Hidden from "@mui/material/Hidden";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { StyledEngineProvider } from "@mui/material/styles";
import { Box, Typography, useMediaQuery } from "@mui/material";
import Navigation from "./navigation";
import NavigationContextProvider from "../context/navigation";
import ConfirmationDialogContext from "../context/confirmation-dialog-context";
import UserProfileDropdown from "./user-profile-dropdown";
import AppSelector from "./app-selector";
import { AuthContext } from "../context/auth";
import { getCookie } from "../App";

const drawerWidth = 224;
const headerHeight = 56;

const drawerPaperSx = {
  width: drawerWidth,
  background: "#0D1017",
  borderRight: "1px solid rgba(255,255,255,0.055)",
  boxShadow: "none",
};

function BaseComponent(props: any) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleCloseDrawer = () => setMobileOpen(false);

  const { user, profileNameLogin, profileImageURLLogin } = useContext(AuthContext);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const currentYear = new Date().getFullYear();
  const whiteLabel = getCookie(process.env.REACT_APP_WHITE_LABEL_COOKIE);
  const container =
    window !== undefined ? () => window().document.body : undefined;

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Profile identity */}
      {profileNameLogin && (
        <Box
          sx={{
            px: 2,
            height: `${headerHeight}px`,
            borderBottom: "1px solid rgba(255,255,255,0.055)",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          {profileImageURLLogin && (
            <Box
              component="img"
              src={profileImageURLLogin}
              alt={profileNameLogin}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                objectFit: "cover",
                flexShrink: 0,
                opacity: 0.9,
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "rgba(255,255,255,0.55)",
              letterSpacing: "0.3px",
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profileNameLogin}
          </Typography>
        </Box>
      )}

      {/* Nav items */}
      <Box sx={{ flex: 1, overflowY: "auto", pt: "8px", pb: "8px" }}>
        <NavigationContextProvider>
          <Navigation onOptionSelect={handleCloseDrawer} />
        </NavigationContextProvider>
      </Box>

      {/* Footer */}
      {!whiteLabel && (
        <Box
          sx={{
            px: 2,
            py: "14px",
            borderTop: "1px solid rgba(255,255,255,0.055)",
          }}
        >
          <Typography
            sx={{
              fontSize: "10.5px",
              color: "rgba(255,255,255,0.2)",
              lineHeight: 1.6,
            }}
          >
            <a
              style={{ color: "rgba(99,102,241,0.6)", textDecoration: "none" }}
              href="https://www.myclientzone.com/"
              target="_blank"
            >
              MyClientZone v5.0
            </a>{" "}
            © {currentYear} Nick Dunn Enterprises Ltd.
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <StyledEngineProvider injectFirst>
      <ConfirmationDialogContext>
        <div
          style={{
            display: "flex",
            minHeight: "100vh",
            width: "100%",
          }}
        >
          <CssBaseline />

          {/* Header */}
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              backgroundColor: "rgba(13, 16, 23, 0.82)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              borderBottom: "1px solid rgba(255,255,255,0.065)",
              boxShadow: "none",
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                height: `${headerHeight}px`,
                minHeight: `${headerHeight}px`,
                px: 0,
              }}
            >
              <Hidden smUp>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerToggle}
                  sx={{
                    ml: "14px",
                    mr: 1,
                    color: "rgba(255,255,255,0.7)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                  }}
                >
                  <MenuIcon sx={{ fontSize: "20px" }} />
                </IconButton>
              </Hidden>

              <Box sx={{ flexGrow: 1 }} />

              <UserProfileDropdown />
              <AppSelector />
            </Toolbar>
          </AppBar>

          {/* Sidebar */}
          <nav>
            <Hidden smUp implementation="css">
              <Drawer
                container={container}
                variant="temporary"
                anchor="left"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                PaperProps={{ sx: drawerPaperSx }}
                ModalProps={{ keepMounted: true }}
              >
                {drawer}
              </Drawer>
            </Hidden>

            <Hidden smDown implementation="css">
              <Drawer
                variant="permanent"
                open
                PaperProps={{ sx: drawerPaperSx }}
              >
                {drawer}
              </Drawer>
            </Hidden>
          </nav>

          {/* Main content */}
          <main
            style={{
              flexGrow: 1,
              marginLeft: isSmallScreen ? 0 : drawerWidth,
              marginTop: `${headerHeight}px`,
              padding: "20px",
              minHeight: "100%",
              width: "100%",
            }}
          >
            {props.children}
          </main>
        </div>
      </ConfirmationDialogContext>
    </StyledEngineProvider>
  );
}

export default BaseComponent;
